import type { LoanSummary } from 'tio-common/types/loan-summary';
import { simulatePlan } from 'tio-employee/demo/repayment-plan-simulator';
import {
  demoScenarioForSelection,
  getDemoDebtLevel,
  type DemoDebtLevelId,
  type DemoSessionSelectionSnapshot,
} from 'tio-employee/utils/career-pathways-demo-data';

export const DEMO_PERSON_LOAN_ID = 'demo-person-loan-1';

export const DEMO_LOAN_ORIGINAL_DATE = '2019-09-01';

export const DEMO_FEDERAL_LOAN_TIME = 'after-october-2007';

// tio-api StrategyEstimator plan keys for the debt axis's plan labels.
export const DEMO_PLAN_KEY_BY_DEBT_PLAN: Record<string, string> = {
  Standard: 'tenYearsStandard',
};

export interface DemoLoanPlan {
  derived: string;
  key: string;
}

// A servicer's IBR label, which tio-api's StrategyEstimator normalizes to 'ibr'.
export const DEMO_PSLF_LOAN_PLAN: DemoLoanPlan = {
  derived: 'Income Based Repayment Plan (IBR)',
  key: 'ibr',
};

export const demoPslfActive = (selection: DemoSessionSelectionSnapshot) =>
  demoScenarioForSelection(selection).features.pslf;

// The person-graph loan's 'repayment-plan-derived' and the loan summary's plan key, from one source.
export function demoLoanPlan(debtRepaymentPlan: string, pslf: boolean): DemoLoanPlan {
  if (pslf) return DEMO_PSLF_LOAN_PLAN;
  return {
    // tio-api maps this label to tenYearsStandard; a bare 'Standard Repayment' reads as ambiguous to it.
    derived: `${debtRepaymentPlan} Repayment Plan`,
    key: DEMO_PLAN_KEY_BY_DEBT_PLAN[debtRepaymentPlan] ?? 'tenYearsStandard',
  };
}

// The NSLDS qualifying-payment count the PSLF tracker shows.
export const DEMO_PSLF_QUALIFYING_PAYMENTS = 78;

export const demoLoanOriginalAmountDollars = (balanceDollars: number) =>
  Math.round(balanceDollars * 1.15);

// Cents and a decimal-fraction rate, as tio-api users_controller#summarize_loans returns them.
export function buildDemoLoanSummary(debtLevelId: DemoDebtLevelId, pslf = false): LoanSummary {
  const debt = getDemoDebtLevel(debtLevelId);
  if (
    !debt.hasLinkedLoans ||
    debt.balanceDollars === null ||
    debt.balanceCents === null ||
    debt.monthlyPaymentCents === null
  ) {
    return { summaryByLoanType: [], errors: [] };
  }
  const plan = demoLoanPlan(debt.repaymentPlan, pslf).key;
  return {
    summaryByLoanType: [
      {
        loanType: 'direct',
        loanIds: [DEMO_PERSON_LOAN_ID],
        currentBalance: debt.balanceCents,
        interestRate: Math.round(debt.interestRate * 100) / 10000,
        monthlyPayment: demoMonthlyPaymentCents(debtLevelId, pslf) ?? debt.monthlyPaymentCents,
        repaymentPlan: plan,
        repaymentPlans: [plan],
        totalDisbursedAmount: demoLoanOriginalAmountDollars(debt.balanceDollars) * 100,
        federalLoanTime: DEMO_FEDERAL_LOAN_TIME,
      },
    ],
    errors: [],
  };
}

export interface DemoHousehold {
  agiCents: number;
  numberOfAdults: number;
  numberOfChildren: number;
  firstLoanTime: string;
  lastLoanTime: string;
  madePayments: number;
  affordPayments: string;
}

const HOUSEHOLD_BASE = {
  firstLoanTime: 'new_ibr_cohort',
  lastLoanTime: 'pre_obbb_cohort',
  affordPayments: 'no',
};

const HOUSEHOLDS: Record<Exclude<DemoDebtLevelId, 'not-linked'>, DemoHousehold> = {
  low: {
    ...HOUSEHOLD_BASE,
    agiCents: 4_850_000,
    numberOfAdults: 1,
    numberOfChildren: 0,
    madePayments: 36,
  },
  medium: {
    ...HOUSEHOLD_BASE,
    agiCents: 6_240_000,
    numberOfAdults: 2,
    numberOfChildren: 1,
    madePayments: 48,
  },
  high: {
    ...HOUSEHOLD_BASE,
    agiCents: 9_600_000,
    numberOfAdults: 2,
    numberOfChildren: 2,
    madePayments: 60,
  },
};

export function demoHousehold(debtLevelId: DemoDebtLevelId): DemoHousehold {
  const level = getDemoDebtLevel(debtLevelId).id;
  return { ...(level === 'not-linked' ? HOUSEHOLDS.medium : HOUSEHOLDS[level]) };
}

// Cents; a PSLF borrower pays the IBR amount the estimators simulate, everyone else the debt level's.
export function demoMonthlyPaymentCents(debtLevelId: DemoDebtLevelId, pslf: boolean) {
  const debt = getDemoDebtLevel(debtLevelId);
  if (!pslf || debt.balanceCents === null) return debt.monthlyPaymentCents;
  const household = demoHousehold(debtLevelId);
  return simulatePlan({
    balanceCents: debt.balanceCents,
    rateAnnual: debt.interestRate / 100,
    agiCents: household.agiCents,
    agiGrowthRate: 0,
    adults: household.numberOfAdults,
    children: household.numberOfChildren,
    paidMonths: DEMO_PSLF_QUALIFYING_PAYMENTS,
    firstLoanTime: household.firstLoanTime,
    lastLoanTime: household.lastLoanTime,
    plan: DEMO_PSLF_LOAN_PLAN.key,
    pslf: true,
    tlfAmountCents: 0,
    multiplier: 1,
  }).summary.first_month_payment;
}
